import { useMemo } from "react";

import { useRouter } from "next/router";

import { useProBuyerViewer } from "@/features/pro-buyer-price/hooks/useProBuyerPrice";
import {
  PRO_BUYER_SEARCH_FILTER_PARAM,
  PRO_BUYER_SEARCH_FILTER_VALUE,
  PRO_BUYER_URL_PARAM,
  PRO_BUYER_URL_VALUE,
} from "@/features/pro-buyer-price/static/pro-buyer-price.static";
import {
  type SearchRoute,
  resolveSearchRoute,
} from "@/features/search/api/route";

/**
 * Resolve the current URL into the API call it stands for. The same function runs in
 * `getServerSideProps`, so the server prefetch and the client query share one key and
 * hydration never triggers a refetch.
 *
 * WHO-5743: `proBuyer=1` adds the participating-sellers filter, client-side and only for a
 * Pro Buyer. The server never knows the viewer, so its render stays the unfiltered search and a
 * Pro Buyer's client refetches once with the filter.
 */
export const useSearchRoute = (): SearchRoute => {
  const { query, locale } = useRouter();
  const { isProBuyer } = useProBuyerViewer();
  const proBuyerFilter =
    isProBuyer && query[PRO_BUYER_URL_PARAM] === PRO_BUYER_URL_VALUE;

  return useMemo(() => {
    const route = resolveSearchRoute(query, locale);

    if (!proBuyerFilter) {
      return route;
    }

    /*
     * The service's generated OpenAPI params do not name the filter yet (the search service adds
     * it in parallel), hence the widening cast; the value is a plain term facet like `category`.
     */
    return {
      ...route,
      params: {
        ...route.params,
        [PRO_BUYER_SEARCH_FILTER_PARAM]: [PRO_BUYER_SEARCH_FILTER_VALUE],
      },
    } as unknown as SearchRoute;
  }, [query, locale, proBuyerFilter]);
};
